// frontend/src/components/PendingDownloadBanner.jsx
//
// Site-wide floating banner that surfaces in-flight single-file
// purchases — the ones tracked in localStorage by pendingDownloads.js.
//
// Why this exists
// ───────────────
// Single-file purchases (USD 1.49 / BRL 3.90) are anonymous-friendly,
// so we can't lean on a server-side "Your downloads" page to remind
// the user that they have a paid file waiting. Without a reminder a
// user who closes the tab right after paying could easily forget the
// purchase — the file is still on the server (7-day TTL) but they'd
// never know to come back for it.
//
// This banner solves that for *every page* on the site:
//   • While ANY record in localStorage is 'pending' (webhook hasn't
//     fired yet) → show a soft "we're confirming your payment" toast.
//   • The moment a record flips to 'ready' (token issued) → show a
//     prominent "Download ready" toast with a 1-click Download button
//     that calls downloadSinglePurchase() directly. No navigation
//     back to /thanks/single required.
//   • The toast is dismissible (×) but the underlying record stays in
//     localStorage so refreshing the page brings it back. The only
//     thing that permanently removes a record is a successful download
//     followed by 7 days of inactivity, or 14 days of pending.
//
// We poll the API every 30 s for pending records (just enough to
// catch the webhook); when the tab is hidden we don't poll at all.
//
// Mounts site-wide in App.js next to <AIAssistant />.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, Download, CheckCircle2, X as XIcon,
  AlertTriangle, ExternalLink,
} from 'lucide-react';
import { useI18n } from '../i18n';
import {
  listPendingDownloads,
  subscribePendingDownloads,
  updatePendingDownload,
  markDownloaded,
  removePendingDownload,
} from '../lib/pendingDownloads';
import {
  getSinglePurchaseBySession,
  downloadSinglePurchase,
} from '../lib/stripe';

// How often we poll the API for unresolved (pending) sessions while
// the tab is visible. The /thanks/single page polls at 2 s — this
// banner is a slower safety net for users who already navigated away.
const POLL_INTERVAL_MS = 30_000;

// Pages where we never render — they have their own download UX.
const HIDE_ON_PATH_PREFIXES = ['/thanks/single', '/thanks/donation'];

export default function PendingDownloadBanner() {
  const { lang } = useI18n();
  const isPT = lang === 'pt';
  const isES = lang === 'es';
  const tx = (pt, en, es) => (isPT ? pt : (isES ? (es || en) : en));

  // sessionIds that the user explicitly hid (× button). Kept in
  // component state only — refresh brings them back so we don't
  // silently drop a paid purchase if the user dismissed it by mistake.
  const [dismissed, setDismissed] = useState(() => new Set());

  // Force a re-read after every subscribePendingDownloads tick.
  const [tick, setTick] = useState(0);
  const [downloadingFor, setDownloadingFor] = useState(null); // sessionId
  const [errorFor, setErrorFor] = useState({}); // sessionId → msg

  // ─── Subscribe to localStorage changes ─────────────────────────
  useEffect(() => {
    return subscribePendingDownloads(() => setTick((n) => n + 1));
  }, []);

  // ─── Slow polling for pending records ──────────────────────────
  //
  // We re-fetch /by-session for every record that's still 'pending'.
  // The /thanks/single page does the heavy lifting; this is the
  // safety net for buyers who already left that page.
  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const pollAllPending = async () => {
      if (cancelled || document.hidden) return;
      const records = listPendingDownloads().filter((r) => r.status === 'pending');
      for (const rec of records) {
        if (cancelled) return;
        try {
          const data = await getSinglePurchaseBySession(rec.sessionId);
          if (cancelled) return;
          if (data?.status === 'succeeded' && data?.download_token) {
            updatePendingDownload(rec.sessionId, {
              status: 'ready',
              downloadToken: data.download_token,
              fileName: data.file_name || rec.fileName,
              fileMime: data.file_mime || rec.fileMime,
              toolSlug: data.tool_slug || rec.toolSlug,
              amountCents: data.amount_cents,
              currency: data.currency,
              maxDownloads: data.max_downloads,
              downloadCount: data.download_count || 0,
              expiresAt: data.expires_at,
            });
          } else if (data?.status === 'failed' || data?.status === 'expired') {
            updatePendingDownload(rec.sessionId, { status: 'failed' });
          }
        } catch (_) {
          // 404 or network — leave the record alone for the next tick.
        }
      }
    };

    const schedule = () => {
      if (cancelled) return;
      timer = setTimeout(async () => {
        await pollAllPending();
        schedule();
      }, POLL_INTERVAL_MS);
    };

    // Run once immediately on mount + when visibility returns.
    pollAllPending();
    schedule();

    const onVisible = () => { if (!document.hidden) pollAllPending(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // ─── Pick which records to render ──────────────────────────────
  // We only show:
  //   • 'ready'   — high-priority "click to download" toast
  //   • 'pending' — soft "we're confirming" hint (collapsible)
  // We also drop records the user × dismissed in this tab, and we
  // never render on the /thanks/single page itself.
  const path = typeof window !== 'undefined' ? window.location.pathname : '';
  const hiddenByRoute = HIDE_ON_PATH_PREFIXES.some((p) => path.startsWith(p));

  // useCallback to read fresh records on every render tick
  // (deps include `tick` so subscribePendingDownloads triggers it).
  const records = useCallback(() => {
    if (hiddenByRoute) return [];
    return listPendingDownloads()
      .filter((r) => r.status === 'pending' || r.status === 'ready')
      .filter((r) => !dismissed.has(r.sessionId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenByRoute, dismissed, tick])();

  const ready = records.filter((r) => r.status === 'ready');
  const pending = records.filter((r) => r.status === 'pending');

  if (records.length === 0) return null;

  // ─── Handlers ───────────────────────────────────────────────────
  const handleDownload = async (rec) => {
    if (!rec?.downloadToken) return;
    const wasFirstDownload = (rec.downloadCount || 0) === 0;
    setDownloadingFor(rec.sessionId);
    setErrorFor((m) => ({ ...m, [rec.sessionId]: '' }));
    try {
      await downloadSinglePurchase(
        rec.downloadToken,
        rec.fileName || 'makercutt-download',
      );
      markDownloaded(rec.sessionId, (rec.downloadCount || 0) + 1);
      // Auto-collapse on the *first* successful download: the user
      // just got what they paid for, so the prominent green toast
      // has done its job. We dismiss it for this tab session — a
      // refresh brings back the compact chip (with "Got it" /
      // "Re-download") if they need to grab the file again.
      if (wasFirstDownload) {
        handleDismiss(rec.sessionId);
      }
    } catch (err) {
      setErrorFor((m) => ({
        ...m,
        [rec.sessionId]: tx(
          'Falha ao baixar. Tente de novo em alguns segundos.',
          'Download failed. Try again in a few seconds.',
          'Falló la descarga. Intenta de nuevo en unos segundos.',
        ),
      }));
    } finally {
      setDownloadingFor(null);
    }
  };

  const handleDismiss = (sessionId) => {
    setDismissed((s) => {
      const next = new Set(s);
      next.add(sessionId);
      return next;
    });
  };

  const handleForget = (sessionId) => {
    // "Forget completely" — only offered for failed/expired records
    // to keep the toast list tidy.
    removePendingDownload(sessionId);
    setDismissed((s) => {
      const next = new Set(s);
      next.add(sessionId);
      return next;
    });
  };

  return (
    <div
      // The Designer IA FAB sits at bottom-5 right-5 with z-[9000] and
      // is roughly 48 px tall. We park the download banner ABOVE the
      // FAB (bottom-20 ≈ 80 px on mobile, bottom-24 ≈ 96 px on desktop)
      // so the two never overlap. z-[1100] is fine because we no
      // longer share the same coordinates — vertical separation is
      // what's keeping them visually distinct.
      className="fixed z-[1100] right-4 sm:right-5 bottom-20 sm:bottom-24 flex flex-col gap-3 items-end max-w-[calc(100vw-2rem)]"
      style={{ pointerEvents: 'none' }}
    >
      {/* Ready-to-download toasts.
       *
       * Two shapes:
       *   • First-time (downloadCount === 0): big, prominent green
       *     toast with the full "Click to download" CTA. This is the
       *     moment the user actually needs to act.
       *   • Already-downloaded (downloadCount >= 1): collapses to a
       *     compact chip ("Arquivo baixado") with three quiet
       *     affordances — re-download (just in case), "Already got
       *     it" (forgets the record permanently, won't haunt the
       *     user again), and the legacy × (dismiss only for this
       *     tab session, refresh brings it back).
       *
       * The collapse logic was added on 2026-05-29 after user feedback
       * that the prominent green toast was annoying when they had
       * already saved the file. */}
      {ready.map((rec) => {
        const errMsg = errorFor[rec.sessionId];
        const isDownloading = downloadingFor === rec.sessionId;
        const maxDl = rec.maxDownloads || 5;
        const dlCount = rec.downloadCount || 0;
        const remaining = Math.max(0, maxDl - dlCount);
        const alreadyDownloaded = dlCount > 0;

        // ── Compact chip after first successful download ──────────
        if (alreadyDownloaded) {
          return (
            <div
              key={rec.sessionId}
              className="pointer-events-auto w-full sm:w-[340px] bg-white border border-emerald-200 rounded-xl shadow-md px-3 py-2.5 flex items-center gap-2.5"
            >
              <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-chunky text-[11px] text-emerald-900 leading-tight uppercase tracking-wide">
                  {tx('Arquivo baixado', 'File downloaded', 'Archivo descargado')}
                </div>
                {rec.fileName && (
                  <div className="text-[10px] text-zinc-500 font-mono truncate mt-0.5">
                    {rec.fileName}
                  </div>
                )}
                {errMsg && (
                  <div className="text-[10px] text-rose-700 mt-0.5 truncate">
                    {errMsg}
                  </div>
                )}
              </div>
              {/* Quiet "re-download" — useful if the file got lost. */}
              <button
                type="button"
                onClick={() => handleDownload(rec)}
                disabled={isDownloading || remaining <= 0}
                title={
                  remaining <= 0
                    ? tx('Limite atingido', 'Limit reached', 'Límite alcanzado')
                    : `${tx('Baixar de novo', 'Download again', 'Descargar de nuevo')} (${remaining}/${maxDl})`
                }
                className={[
                  'shrink-0 p-1.5 rounded-md transition-colors',
                  isDownloading || remaining <= 0
                    ? 'text-zinc-300 cursor-not-allowed'
                    : 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50',
                ].join(' ')}
                aria-label={tx('Baixar de novo', 'Download again', 'Descargar de nuevo')}
              >
                {isDownloading
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Download className="w-4 h-4" />}
              </button>
              {/* "Already got it" — forgets the record permanently so
                  the banner never returns for this session. */}
              <button
                type="button"
                onClick={() => handleForget(rec.sessionId)}
                title={tx('Já baixei — não mostrar mais', 'Already got it — don\'t show again', 'Ya lo tengo — no mostrar más')}
                className="shrink-0 text-[10px] font-chunky uppercase tracking-wider px-2 py-1 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
              >
                {tx('Já baixei', 'Got it', 'Ya lo tengo')}
              </button>
            </div>
          );
        }

        // ── Big toast for the FIRST download (untouched flow) ─────
        return (
          <div
            key={rec.sessionId}
            className="pointer-events-auto w-full sm:w-[360px] bg-white border border-emerald-200 rounded-2xl shadow-[0_10px_30px_-10px_rgba(16,185,129,0.4)] overflow-hidden"
          >
            <div className="px-4 py-3 bg-emerald-50 border-b border-emerald-100 flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-chunky text-sm text-emerald-900 leading-tight">
                  {tx('Seu download está pronto', 'Your download is ready', 'Tu descarga está lista')}
                </div>
                {rec.fileName && (
                  <div className="text-xs text-emerald-700/80 font-mono truncate mt-0.5">
                    {rec.fileName}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => handleDismiss(rec.sessionId)}
                aria-label={tx('Dispensar', 'Dismiss', 'Descartar')}
                className="shrink-0 -mr-1 -mt-1 p-1 rounded text-emerald-700/70 hover:text-emerald-900 hover:bg-emerald-100"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-xs text-zinc-600 leading-relaxed">
                {tx(
                  'Pagamento confirmado. Clique para baixar — o link fica válido por 7 dias neste navegador.',
                  'Payment confirmed. Click to download — the link stays valid for 7 days in this browser.',
                  'Pago confirmado. Haz clic para descargar — el enlace queda válido por 7 días en este navegador.',
                )}
              </p>
              <button
                type="button"
                onClick={() => handleDownload(rec)}
                disabled={isDownloading || remaining <= 0}
                className={[
                  'w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg font-chunky text-xs uppercase tracking-wider transition-colors',
                  isDownloading || remaining <= 0
                    ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                    : 'bg-emerald-600 hover:bg-emerald-500 text-white',
                ].join(' ')}
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {tx('Baixando…', 'Downloading…', 'Descargando…')}
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    {tx('Baixar agora', 'Download now', 'Descargar ahora')}
                  </>
                )}
              </button>
              {errMsg && (
                <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                  {errMsg}
                </div>
              )}
              {/* Escape hatch: user might have already saved the file
                  via the /thanks/single page or a previous session
                  before we tracked the download count. Letting them
                  forget the record permanently is friendlier than
                  forcing them to dismiss it on every page load. */}
              <button
                type="button"
                onClick={() => handleForget(rec.sessionId)}
                className="w-full text-[10px] font-chunky uppercase tracking-wider text-zinc-500 hover:text-zinc-900 transition-colors"
              >
                {tx(
                  'Já baixei — não mostrar mais',
                  'Already got it — don\'t show again',
                  'Ya lo tengo — no mostrar más',
                )}
              </button>
              <div className="text-[10px] text-zinc-500 text-center">
                {remaining} {tx('de', 'of', 'de')} {maxDl}{' '}
                {tx('downloads restantes', 'downloads remaining', 'descargas restantes')}
              </div>
            </div>
          </div>
        );
      })}

      {/* Pending toasts: smaller, soft, no button — just a heads-up
          that we're confirming. Clicking opens /thanks/single so the
          page can take over with full polling + auto-download. */}
      {pending.map((rec) => (
        <Link
          key={rec.sessionId}
          to={`/thanks/single?session_id=${encodeURIComponent(rec.sessionId)}`}
          className="pointer-events-auto w-full sm:w-[320px] bg-white border border-sky-200 rounded-2xl shadow-md px-4 py-3 flex items-center gap-3 hover:border-sky-400 hover:shadow-lg transition-all group"
        >
          <div className="shrink-0 w-9 h-9 rounded-full bg-sky-100 text-sky-700 inline-flex items-center justify-center">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-chunky text-xs text-sky-900 leading-tight">
              {tx('Confirmando seu pagamento…', 'Confirming your payment…', 'Confirmando tu pago…')}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5 truncate">
              {rec.fileName || tx('Seu arquivo está sendo preparado', 'Your file is being prepared', 'Tu archivo está siendo preparado')}
            </div>
          </div>
          <ExternalLink className="shrink-0 w-3.5 h-3.5 text-sky-700/60 group-hover:text-sky-700" />
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDismiss(rec.sessionId);
            }}
            aria-label={tx('Dispensar', 'Dismiss', 'Descartar')}
            className="shrink-0 -mr-1 p-1 rounded text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
          >
            <XIcon className="w-3.5 h-3.5" />
          </button>
        </Link>
      ))}

      {/* Failed toasts: rare, but if we did transition to 'failed' we
          let the user clear it instead of haunting them. */}
      {records
        .filter((r) => r.status === 'failed')
        .map((rec) => (
          <div
            key={rec.sessionId}
            className="pointer-events-auto w-full sm:w-[320px] bg-white border border-rose-200 rounded-2xl shadow-md px-4 py-3 flex items-center gap-3"
          >
            <div className="shrink-0 w-9 h-9 rounded-full bg-rose-100 text-rose-700 inline-flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-chunky text-xs text-rose-900 leading-tight">
                {tx('Pagamento não confirmado', 'Payment not confirmed', 'Pago no confirmado')}
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleForget(rec.sessionId)}
              className="text-[11px] font-chunky uppercase tracking-wider text-rose-700 hover:text-rose-900"
            >
              {tx('Limpar', 'Clear', 'Borrar')}
            </button>
          </div>
        ))}
    </div>
  );
}
